import React, { useRef, useState } from "react";
import { ActionIcon, FileButton, Group, Textarea } from "@mantine/core";
import { IconPaperclip, IconSend } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { useChat } from "./ChatContext";
import ChatPendingFiles from "./ChatPendingFiles";

export default function ChatComposer() {
  const { t } = useTranslation();
  const { sendMessage, sending, notifyTyping } = useChat();
  const [body, setBody] = useState("");
  const [files, setFiles] = useState([]);
  const resetRef = useRef(null);
  const lastTypingAt = useRef(0);

  function onSend() {
    if (!body.trim() && !files.length) return;
    sendMessage(body, files).then(() => {
      setBody("");
      setFiles([]);
      resetRef.current?.();
    });
  }

  function onBodyChange(event) {
    const value = event.currentTarget.value;
    setBody(value);
    if (!value.trim()) return;
    const now = Date.now();
    if (now - lastTypingAt.current < 1000) return;
    lastTypingAt.current = now;
    if (notifyTyping) notifyTyping();
  }

  return (
    <div style={{ borderTop: "1px solid var(--mantine-color-gray-3)", padding: 8 }}>
      <ChatPendingFiles
        files={files}
        onRemove={setFiles}
        onClearAll={() => resetRef.current?.()}
      />
      <Group align="flex-end" wrap="nowrap" gap="xs">
        <FileButton
          resetRef={resetRef}
          multiple
          onChange={(picked) => setFiles(picked || [])}
        >
          {(props) => (
            <ActionIcon {...props} variant="subtle" aria-label={t("chat_attach")}>
              <IconPaperclip size={18} />
            </ActionIcon>
          )}
        </FileButton>
        <Textarea
          style={{ flex: 1 }}
          autosize
          minRows={1}
          maxRows={4}
          placeholder={t("chat_message_placeholder")}
          value={body}
          onChange={onBodyChange}
          onKeyDown={(event) => {
            if (event.key === "Enter" && !event.shiftKey) {
              event.preventDefault();
              onSend();
            }
          }}
        />
        <ActionIcon
          color="blue"
          variant="filled"
          onClick={onSend}
          loading={sending}
          aria-label={t("chat_send")}
        >
          <IconSend size={16} />
        </ActionIcon>
      </Group>
    </div>
  );
}
